import { useRef, useEffect, useMemo } from 'react';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { withWindow } from '@shared/utils/withWindow';
import { throttle } from 'lodash-es';

const RESIZE_THROTTLE_MS = 150;

/**
 * Observer callback function type.
 * Called when element position or size changes.
 */
export type ObserverCallback = () => void;

/**
 * Cleanup function returned from observe subscription.
 */
export type Unsubscribe = () => void;

/**
 * Hook return type - function to subscribe to element changes.
 * Function types are immutable by nature, so no Readonly wrapper needed.
 */
export type ObserveFunction = (callback: ObserverCallback) => Unsubscribe;

/**
 * Generic hook for observing element position and size changes.
 *
 * Uses ResizeObserver for element-specific size changes when available.
 * Falls back to window resize listener for browsers without ResizeObserver support.
 *
 * @param element - The HTML element to observe, or null to disable observation
 * @returns Function to subscribe callbacks to element changes
 */
export const useElementObserver = (element: HTMLElement | null): ObserveFunction => {
  const observersRef = useRef<Set<ObserverCallback>>(new Set());
  const resizeObserverRef = useRef<ResizeObserver | null>(null);
  const elementRef = useRef<HTMLElement | null>(element);

  // Update element ref when it changes
  useEffect(() => {
    elementRef.current = element;
  }, [element]);

  // Notify all observers when element changes
  const notifyObservers = useEventCallback(() => {
    observersRef.current.forEach(cb => cb());
  });

  // Setup ResizeObserver or resize fallback for element-specific size changes
  useEffect(() => {
    const currentElement = elementRef.current;
    if (!currentElement) {
      // Cleanup if element is removed
      if (resizeObserverRef.current) {
        resizeObserverRef.current.disconnect();
        resizeObserverRef.current = null;
      }
      return;
    }

    const hasResizeObserver = typeof ResizeObserver !== 'undefined';

    if (hasResizeObserver) {
      // eslint-disable-next-line compat/compat
      resizeObserverRef.current = new ResizeObserver(() => {
        notifyObservers();
      });
      resizeObserverRef.current.observe(currentElement);

      // Return cleanup function
      return () => {
        if (resizeObserverRef.current) {
          resizeObserverRef.current.disconnect();
          resizeObserverRef.current = null;
        }
      };
    }

    // Fallback: use window resize listener for browsers without ResizeObserver
    const cleanup = withWindow(
      global => {
        const resizeHandler = throttle(() => {
          // Only notify if element still exists
          if (elementRef.current) {
            notifyObservers();
          }
        }, RESIZE_THROTTLE_MS);

        global.addEventListener('resize', resizeHandler, { passive: true });

        return () => {
          resizeHandler.cancel();
          global.removeEventListener('resize', resizeHandler);
        };
      },
      () => {},
    );

    return cleanup;
  }, [element, notifyObservers]);

  // Return stable observe function
  return useMemo(
    () => {
      const observe: ObserveFunction = (callback: ObserverCallback) => {
        observersRef.current.add(callback);
        return () => {
          observersRef.current.delete(callback);
        };
      };
      return observe;
    },
    [], // Stable reference - never changes
  );
};
